import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { DEV_BYPASS_AUTH, DEV_MOCK_USER } from "@/lib/auth-config";
import { purgeAllAntichambreStorage } from "@/hooks/useAntichambreSession";

type AuthContextValue = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
  ) => Promise<{ needsConfirmation: boolean; alreadyRegistered: boolean }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  resendConfirmation: (email: string) => Promise<void>;
  /** Le lien de connexion par email. Voir le commentaire de l'implémentation. */
  envoyerLienDeConnexion: (email: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(!DEV_BYPASS_AUTH);
  const prevUserIdRef = useRef<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (DEV_BYPASS_AUTH) return;
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      const nextId = s?.user?.id ?? null;
      // Si l'identité change (login d'un autre user, logout) → purge les
      // clés antichambre locales pour éviter de pré-remplir avec une saisine
      // d'un autre compte, ET vide le cache react-query pour ne jamais
      // afficher le tier d'abonnement / profil du user précédent
      // (sprint BADGE-CACHE-SCOPE 2026-06-22).
      if (prevUserIdRef.current !== nextId) {
        purgeAllAntichambreStorage();
        queryClient.clear();
        prevUserIdRef.current = nextId;
      }
      setSession(s);
    });
    supabase.auth.getSession().then(({ data }) => {
      prevUserIdRef.current = data.session?.user?.id ?? null;
      setSession(data.session);
      setLoading(false);
    });
    // "Rester connecté" : si le drapeau n'est pas posé, on déconnecte
    // la session à la fermeture de l'onglet/navigateur.
    const onPageHide = () => {
      try {
        if (typeof window !== "undefined" && localStorage.getItem("qorum.auth.remember") !== "1") {
          void supabase.auth.signOut();
        }
      } catch {
        /* storage indisponible */
      }
    };
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", onPageHide);
    }
    return () => {
      sub.subscription.unsubscribe();
      if (typeof window !== "undefined") {
        window.removeEventListener("pagehide", onPageHide);
      }
    };
  }, []);

  const mockUser = DEV_BYPASS_AUTH ? (DEV_MOCK_USER as unknown as User) : null;

  const value: AuthContextValue = {
    user: mockUser ?? session?.user ?? null,
    session,
    loading,
    async signIn(email, password) {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    },
    async signUp(email, password) {
      const redirectTo =
        typeof window !== "undefined" ? `${window.location.origin}/auth/callback` : undefined;
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: redirectTo },
      });
      if (error) throw error;
      // Supabase (protection anti-énumération) ne renvoie PAS d'erreur quand
      // l'email est déjà inscrit + confirmé : il renvoie un user avec
      // `identities: []`. C'est le seul signal fiable pour le détecter.
      const alreadyRegistered =
        !data.session &&
        Array.isArray(data.user?.identities) &&
        data.user!.identities!.length === 0;
      return { needsConfirmation: !data.session, alreadyRegistered };
    },
    async signOut() {
      purgeAllAntichambreStorage();
      await supabase.auth.signOut();
    },
    async resetPassword(email) {
      const redirectTo =
        typeof window !== "undefined" ? `${window.location.origin}/reset-password` : undefined;
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
      if (error) throw error;
    },
    async updatePassword(password) {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
    },
    /**
     * LE LIEN DE CONNEXION — plus aucun mot de passe à saisir.
     *
     * ── DEUX CHOIX QUI SE VOIENT DANS LE CODE ────────────────────────────────────────────────
     *
     * ① `shouldCreateUser: false`. Un email inconnu NE CRÉE PAS de compte en silence. Le défaut de
     *    Supabase est l'inverse : demander un lien avec une adresse au hasard y ouvrirait un compte,
     *    et une inscription qui se produit sans que personne ne l'ait voulue est une inscription
     *    qu'on ne peut ni expliquer ni facturer.
     *
     * ② L'ERREUR « utilisateur inconnu » EST AVALÉE, et c'est délibéré. La renvoyer à l'écran
     *    dirait à n'importe qui si une adresse a un compte chez nous — un oracle d'existence que
     *    l'on offrirait gratuitement. L'écran affiche donc la MÊME phrase dans les deux cas :
     *    « si un compte existe pour cette adresse, le lien vient de partir ».
     *
     * Ce que cela coûte, et il faut le savoir : une personne qui se trompe d'adresse attendra un
     * email qui ne viendra jamais, sans qu'on le lui dise. C'est le prix de la non-divulgation, et
     * il se paie du bon côté — l'inconfort est pour celui qui se trompe, jamais pour le tiers dont
     * on aurait révélé le compte.
     */
    async envoyerLienDeConnexion(email) {
      const emailRedirectTo =
        typeof window !== "undefined" ? `${window.location.origin}/auth/callback` : undefined;
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo, shouldCreateUser: false },
      });
      if (!error) return;
      // Compte inexistant : silence volontaire (cf. ② ci-dessus).
      const inconnu =
        error.code === "user_not_found" ||
        error.code === "otp_disabled" ||
        /user not found|signups not allowed/i.test(error.message ?? "");
      if (inconnu) return;
      // Tout le reste (réseau, quota d'envoi, adresse invalide) se DIT : ce sont des états que
      // l'utilisateur peut corriger, et les taire lui ferait attendre pour rien.
      throw error;
    },
    async resendConfirmation(email) {
      const emailRedirectTo =
        typeof window !== "undefined" ? `${window.location.origin}/auth/callback` : undefined;
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: { emailRedirectTo },
      });
      if (error) throw error;
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
